import { apiService } from './api';

/**
 * Frontend client for the certificate reclamation (complaint / revocation) API.
 *  - Clients open reclamations on their own certificates and track them.
 *  - Admin / supervisors triage, accept (revoke + reopen) or reject them.
 */

export type ReclamationStatus =
  | 'OUVERTE'
  | 'EN_COURS'
  | 'ACCEPTEE'
  | 'REJETEE'
  | 'CLOTUREE';

export type ReclamationReason =
  | 'RESULTAT_INCOHERENT'
  | 'ELEMENT_MANQUANT'
  | 'INFO_CLIENT_ERRONEE'
  | 'DELAI'
  | 'AUTRE';

export const RECLAMATION_REASONS: { value: ReclamationReason; label: string }[] = [
  { value: 'RESULTAT_INCOHERENT', label: 'Résultat incohérent / douteux' },
  { value: 'ELEMENT_MANQUANT', label: 'Élément ou analyse manquant(e)' },
  { value: 'INFO_CLIENT_ERRONEE', label: 'Informations client / projet erronées' },
  { value: 'DELAI', label: 'Délai / certificat reçu en retard' },
  { value: 'AUTRE', label: 'Autre (préciser)' },
];

export const RECLAMATION_STATUS_LABEL: Record<ReclamationStatus, string> = {
  OUVERTE: 'Ouverte',
  EN_COURS: 'En cours',
  ACCEPTEE: 'Acceptée — réanalyse',
  REJETEE: 'Rejetée',
  CLOTUREE: 'Clôturée — certificat réémis',
};

export interface CertificateReclamation {
  id: string;
  tenant_id?: string | null;
  certificate_id: string;
  sample_id: string;
  client_id?: string | null;
  certificate_number?: string | null;
  status: ReclamationStatus;
  reason_category: ReclamationReason;
  reason_text: string;
  opened_by?: string | null;
  opened_at: string;
  assigned_to?: string | null;
  assigned_at?: string | null;
  resolution_comment?: string | null;
  revision_comment?: string | null;
  target_laboratory?: string | null;
  resolved_by?: string | null;
  resolved_at?: string | null;
  new_certificate_id?: string | null;
  new_certificate_number?: string | null;
  created_at: string;
  updated_at: string;
}

class ReclamationsService {
  private baseUrl = '/reclamations';

  // ── Client ────────────────────────────────────────────────────────────
  async open(payload: {
    certificate_id: string;
    reason_category: ReclamationReason;
    reason_text: string;
  }): Promise<CertificateReclamation> {
    return apiService.post<CertificateReclamation>(this.baseUrl, payload);
  }

  async listMine(): Promise<CertificateReclamation[]> {
    return apiService.get<CertificateReclamation[]>(`${this.baseUrl}/mine`);
  }

  // ── Admin / supervisor ──────────────────────────────────────────────────
  async list(status?: string): Promise<CertificateReclamation[]> {
    const qs = status ? `?status=${encodeURIComponent(status)}` : '';
    return apiService.get<CertificateReclamation[]>(`${this.baseUrl}${qs}`);
  }

  async getOne(id: string): Promise<CertificateReclamation> {
    return apiService.get<CertificateReclamation>(`${this.baseUrl}/${id}`);
  }

  async assign(id: string): Promise<CertificateReclamation> {
    return apiService.post<CertificateReclamation>(`${this.baseUrl}/${id}/assign`, {});
  }

  async resolve(
    id: string,
    payload: {
      decision: 'accept' | 'reject';
      resolution_comment?: string;
      target_laboratory?: string;
    },
  ): Promise<CertificateReclamation> {
    return apiService.post<CertificateReclamation>(`${this.baseUrl}/${id}/resolve`, payload);
  }

  async setRevisionComment(id: string, revision_comment: string): Promise<CertificateReclamation> {
    return apiService.post<CertificateReclamation>(`${this.baseUrl}/${id}/revision-comment`, {
      revision_comment,
    });
  }
}

export const reclamationsService = new ReclamationsService();
export default reclamationsService;

